import React, { useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import styles from './Auth.module.scss';
import { useProfiles } from '../../hooks/queries/useProfiles';
import { tokens } from '../../styles/tokens';
import { PasswordRequirements, isPasswordStrong } from './PasswordRequirements';
import { ConfirmEmail } from './ConfirmEmail';
import { CONTACT_EMAIL, CONTACT_MAILTO } from '../../utils/contactInfo';

type Step = 'signup' | 'confirm-email' | 'setup';

export const RegisterScreen: React.FC = () => {
  const [step, setStep] = useState<Step>('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [kidNames, setKidNames] = useState<string[]>(['']);

  const { signUp } = useAuth();
  const { createProfile } = useProfiles();
  const navigate = useNavigate();

  const passwordStrong = useMemo(() => isPasswordStrong(password), [password]);
  const passwordsMatch = confirmPassword.length === 0 || password === confirmPassword;

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!passwordStrong) {
      setError('Please meet all password requirements before continuing.');
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }
    if (!acceptTerms) {
      setError('Please accept the Terms of Service and Privacy Policy to continue.');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await signUp(email, password);
      if (result.error) {
        setError(result.error.message);
      } else if (result.needsEmailConfirmation) {
        setStep('confirm-email');
      } else {
        setStep('setup');
      }
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNameChange = (index: number, value: string) => {
    const newNames = [...kidNames];
    newNames[index] = value;
    setKidNames(newNames);
  };

  const addKidField = () => setKidNames([...kidNames, '']);

  const removeKidField = (index: number) => {
    if (kidNames.length > 1) {
      setKidNames(kidNames.filter((_, i) => i !== index));
    }
  };

  const handleSetupComplete = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const validNames = kidNames.filter((n) => n.trim() !== '');
      if (validNames.length === 0) {
        setError('Please add at least one child profile.');
        setIsSubmitting(false);
        return;
      }

      for (const name of validNames) {
        await createProfile({ displayName: name.trim() });
      }

      navigate('/app');
    } catch (err) {
      console.error(err);
      setError('Failed to create profiles. You can add them later in settings.');
      setTimeout(() => navigate('/app'), 2000);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (step === 'confirm-email') {
    return <ConfirmEmail email={email} />;
  }

  if (step === 'setup') {
    return (
      <div className={styles.authContainer}>
        <div className={styles.loginSplitView}>
          <div className={styles.heroSection}>
            <div className={styles.logoContainer}>
              <span className={styles.logoEmoji}>👨‍👩‍👧‍👦</span>
              <h1 className={styles.brandName}>Welcome!</h1>
            </div>
            <h2 className={styles.tagline}>Let's set up your family.</h2>
            <p className={styles.description}>
              Who will be using MyMinu? Add a profile for each child.
            </p>
          </div>

          <div className={styles.card}>
            <h3>Add Children</h3>
            <p className={styles.subText}>You can add more later.</p>

            {error && (
              <div className={styles.error} role="alert" aria-live="polite">
                {error}
              </div>
            )}

            <form onSubmit={handleSetupComplete} className={styles.form}>
              <div className={styles.inputGroup}>
                <label>Child Names</label>
                {kidNames.map((name, index) => (
                  <div
                    key={index}
                    style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}
                  >
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => handleNameChange(index, e.target.value)}
                      placeholder={`Child ${index + 1} Name`}
                      required={index === 0}
                      autoFocus={index === kidNames.length - 1}
                      autoCapitalize="words"
                      autoComplete="off"
                    />
                    {kidNames.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeKidField(index)}
                        style={{ background: 'none', border: 'none', color: tokens.softRed, cursor: 'pointer', fontSize: '18px' }}
                        aria-label={`Remove child ${index + 1}`}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addKidField}
                  style={{
                    background: 'none',
                    border: '1px dashed #cbd5e0',
                    borderRadius: '8px',
                    padding: '8px',
                    width: '100%',
                    color: '#4a5568',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  + Add another child
                </button>
              </div>

              <button type="submit" className={styles.button} disabled={isSubmitting}>
                {isSubmitting ? 'Setting up…' : 'Get Started 🚀'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.authContainer}>
      <div className={styles.loginSplitView}>
        <div className={styles.heroSection}>
          <div className={styles.logoContainer}>
            <span className={styles.logoEmoji}>🚀</span>
            <h1 className={styles.brandName}>Join MyMinu</h1>
          </div>
          <h2 className={styles.tagline}>Start Your Time Adventure</h2>
          <p className={styles.description}>
            Create an account to start building better habits and happier mornings for your family.
          </p>
        </div>

        <div className={styles.card}>
          <h3>Create Account</h3>
          <p className={styles.subText}>It only takes a minute!</p>

          {error && (
            <div
              className={styles.error}
              role="alert"
              aria-live="polite"
              id="register-error"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSignup} className={styles.form} noValidate>
            <div className={styles.inputGroup}>
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="parent@example.com"
                autoComplete="email"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="password">Password</label>
              <div className={styles.passwordWrapper}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  autoComplete="new-password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  aria-describedby="password-requirements"
                />
                <button
                  type="button"
                  className={styles.passwordToggle}
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              <PasswordRequirements password={password} id="password-requirements" />
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirm-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="••••••••"
                autoComplete="new-password"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                aria-invalid={!passwordsMatch}
                aria-describedby={!passwordsMatch ? 'confirm-password-error' : undefined}
              />
              {!passwordsMatch && (
                <p
                  id="confirm-password-error"
                  role="alert"
                  aria-live="polite"
                  className={styles.fieldError}
                >
                  Passwords don't match
                </p>
              )}
            </div>

            <div className={styles.consentRow}>
              <input
                id="acceptTerms"
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                required
              />
              <label htmlFor="acceptTerms">
                I agree to the <Link to="/legal/terms">Terms of Service</Link> and{' '}
                <Link to="/legal/privacy">Privacy Policy</Link>.
              </label>
            </div>

            <button
              type="submit"
              className={styles.button}
              disabled={
                isSubmitting ||
                !passwordStrong ||
                !passwordsMatch ||
                !acceptTerms
              }
            >
              {isSubmitting ? 'Creating Account…' : 'Create account'}
            </button>
          </form>

          <div className={styles.link}>
            Already have an account? <Link to="/login">Login</Link>
          </div>
          <div className={styles.link} style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
            Need help? <a href={CONTACT_MAILTO}>{CONTACT_EMAIL}</a>
          </div>
        </div>
      </div>
    </div>
  );
};
